<template>
  <div
    class="subtitles-menu"
    ref="containerRef"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <button
      class="control-button"
      :class="{ 'control-button--active': player.subtitlesEnabled.value }"
      @click="handleClick"
      @keydown.arrow-down.prevent="openMenuAndFocus"
      :aria-label="buttonAriaLabel"
      :data-tooltip="buttonTooltip"
      :aria-pressed="hasMultipleTracks ? undefined : player.subtitlesEnabled.value"
      :aria-expanded="hasMultipleTracks ? isMenuOpen : undefined"
      :aria-haspopup="hasMultipleTracks ? 'menu' : undefined"
      ref="buttonRef"
      v-html="player.subtitlesEnabled.value ? closedCaptionsIcon : closedCaptionsOffIcon"
    />

    <div
      v-if="isMenuOpen"
      class="subtitles-menu__popup"
      role="menu"
      :aria-label="t('player.controls.subtitles')"
      ref="popupRef"
      @keydown="onMenuKeydown"
    >
      <button
        v-for="track in player.availableSubtitleTracks.value"
        :key="track.index"
        class="subtitles-menu__item"
        :class="{ 'subtitles-menu__item--active': isTrackActive(track.index) }"
        role="menuitemradio"
        :aria-checked="isTrackActive(track.index)"
        @click.stop="selectTrack(track.index)"
      >
        <span
          class="subtitles-menu__check"
          aria-hidden="true"
          v-html="checkIcon"
        />
        <span class="subtitles-menu__label">{{ track.name || track.language }}</span>
        <span
          v-if="track.isAutoGenerated"
          class="subtitles-menu__badge"
        >{{ t('player.panels.subtitles.autoGeneratedBadge') }}</span>
      </button>

      <div class="subtitles-menu__separator" role="separator" />

      <button
        class="subtitles-menu__item"
        :class="{ 'subtitles-menu__item--active': isOffActive }"
        role="menuitemradio"
        :aria-checked="isOffActive"
        @click.stop="selectOff"
      >
        <span
          class="subtitles-menu__check"
          aria-hidden="true"
          v-html="checkIcon"
        />
        <span class="subtitles-menu__label">{{ t('player.panels.subtitles.off') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue'
import { closedCaptionsIcon, closedCaptionsOffIcon, checkIcon } from '../../assets/icons'
import { useVideoI18n } from '../../i18n'
import type { UseVideoPlayerReturn } from '../../composables/useVideoPlayer'

const props = defineProps<{
  player: UseVideoPlayerReturn
}>()

const { t } = useVideoI18n()

const containerRef = ref<HTMLElement | null>(null)
const buttonRef = ref<HTMLButtonElement | null>(null)
const popupRef = ref<HTMLElement | null>(null)
const isMenuOpen = ref(false)

let hoverOpenTimer: ReturnType<typeof setTimeout> | null = null
let hoverCloseTimer: ReturnType<typeof setTimeout> | null = null

const isTouchDevice = computed(() =>
  typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
)

const hasMultipleTracks = computed(() =>
  props.player.availableSubtitleTracks.value.length > 1,
)

const isOffActive = computed(() =>
  !props.player.subtitlesEnabled.value || props.player.currentSubtitleTrackIndex.value === -1,
)

function isTrackActive(trackIndex: number): boolean {
  return props.player.subtitlesEnabled.value
    && props.player.currentSubtitleTrackIndex.value === trackIndex
}

const buttonAriaLabel = computed(() => {
  if (hasMultipleTracks.value) {
    return t('player.controls.subtitles')
  }
  return props.player.subtitlesEnabled.value
    ? t('player.controls.subtitlesDisable')
    : t('player.controls.subtitlesEnable')
})

const buttonTooltip = computed(() => {
  if (hasMultipleTracks.value) {
    return `${t('player.controls.subtitles')} (c)`
  }
  return (props.player.subtitlesEnabled.value
    ? t('player.controls.subtitlesDisable')
    : t('player.controls.subtitlesEnable')) + ' (c)'
})

// ── Click handling ──

function handleClick() {
  if (isTouchDevice.value && hasMultipleTracks.value) {
    toggleMenu()
  } else {
    props.player.toggleSubtitles()
    closeMenu()
  }
}

// ── Hover handling (desktop only) ──

function onMouseEnter() {
  if (isTouchDevice.value || !hasMultipleTracks.value) return
  clearTimers()
  hoverOpenTimer = setTimeout(() => {
    isMenuOpen.value = true
  }, 200)
}

function onMouseLeave() {
  if (isTouchDevice.value) return
  clearTimers()
  hoverCloseTimer = setTimeout(() => {
    isMenuOpen.value = false
  }, 300)
}

function clearTimers() {
  if (hoverOpenTimer) { clearTimeout(hoverOpenTimer); hoverOpenTimer = null }
  if (hoverCloseTimer) { clearTimeout(hoverCloseTimer); hoverCloseTimer = null }
}

// ── Menu actions ──

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value
}

function closeMenu() {
  isMenuOpen.value = false
}

function selectTrack(trackIndex: number) {
  props.player.enableSubtitles(trackIndex)
  closeMenu()
}

function selectOff() {
  props.player.disableSubtitles()
  closeMenu()
}

// ── Keyboard navigation ──

function openMenuAndFocus() {
  if (!hasMultipleTracks.value) return
  isMenuOpen.value = true
  nextTick(() => {
    const items = getMenuItems()
    const activeIndex = items.findIndex(el => el.classList.contains('subtitles-menu__item--active'))
    const target = activeIndex >= 0 ? items[activeIndex] : items[0]
    target?.focus()
  })
}

function onMenuKeydown(e: KeyboardEvent) {
  const items = getMenuItems()
  const currentIndex = items.indexOf(document.activeElement as HTMLElement)

  switch (e.key) {
    case 'ArrowDown': {
      e.preventDefault()
      const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0
      items[next]?.focus()
      break
    }
    case 'ArrowUp': {
      e.preventDefault()
      const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1
      items[prev]?.focus()
      break
    }
    case 'Escape':
      e.preventDefault()
      closeMenu()
      buttonRef.value?.focus()
      break
    case 'Tab':
      closeMenu()
      break
  }
}

function getMenuItems(): HTMLElement[] {
  if (!popupRef.value) return []
  return Array.from(popupRef.value.querySelectorAll<HTMLElement>('[role="menuitemradio"]'))
}

// ── Click outside ──

function onDocumentClick(e: MouseEvent) {
  if (containerRef.value && !containerRef.value.contains(e.target as Node)) {
    closeMenu()
  }
}

watch(isMenuOpen, (open) => {
  if (open) {
    document.addEventListener('click', onDocumentClick, true)
  } else {
    document.removeEventListener('click', onDocumentClick, true)
  }
})

onBeforeUnmount(() => {
  clearTimers()
  document.removeEventListener('click', onDocumentClick, true)
})
</script>

<style scoped lang="scss">
.subtitles-menu {
  position: relative;

  &__popup {
    position: absolute;
    bottom: calc(100% + 8px);
    right: 0;
    min-width: 200px;
    max-width: 300px;
    background: var(--vp-panel-bg, rgba(0, 0, 0, 0.95));
    border: 1px solid var(--vp-border-default);
    border-radius: 8px;
    padding: 4px 0;
    z-index: 100;
    backdrop-filter: blur(8px);
    animation: subtitles-menu-enter 0.15s ease;
  }

  &__separator {
    height: 1px;
    margin: 4px 12px;
    background: var(--vp-border-default);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: none;
    color: var(--vp-text-tertiary);
    font-size: 0.8125rem;
    font-family: inherit;
    cursor: pointer;
    text-align: left;
    white-space: nowrap;
    transition: background-color 0.1s ease;

    &:hover {
      background: var(--vp-surface-hover);
    }

    &--active {
      color: var(--vp-text-primary);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: -2px;
    }
  }

  &__check {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    visibility: hidden;

    .subtitles-menu__item--active & {
      visibility: visible;
    }
  }

  &__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__badge {
    flex-shrink: 0;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.75rem;
    line-height: 1.4;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: var(--vp-text-tertiary);
    background: var(--vp-surface-hover, rgba(255, 255, 255, 0.12));
    text-transform: none;
  }
}

@keyframes subtitles-menu-enter {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .subtitles-menu__popup {
    animation: none;
  }

  .subtitles-menu__item {
    transition: none;
  }
}
</style>
